/**
 * The only file that touches `localStorage` and `sessionStorage` (lint-enforced, LLD §3.4). Every
 * key the dashboard stores is listed here, so "what does this app keep on a
 * shared machine" has one answer. The access token is never one of them.
 */
export const STORAGE_KEYS = {
  theme: "devsms.theme",
  language: "devsms.language",
  /** A flag, "1", never the token: picks index.html's static first paint (src/auth/session-hint.ts). */
  session: "devsms.hasSession",
  /** A flag, "1": a sign-out whose logout did not reach the server, sent at the next boot. */
  logoutPending: "devsms.logoutPending",
  /** The cross-tab refresh lease without Web Locks: an owner id and an expiry, never a token. */
  refreshLease: "devsms.refreshLease",
  /** The command palette's recent entries per user id: ids and labels only (LLD §7.5). */
  paletteRecent: "devsms.palette.recent",
  /** The sidebar's collapsed state, "true" or "false" (LLD §7.4). */
  sidebar: "devsms.sidebar",
} as const

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS]

/** `null` when absent or when storage is unavailable (private mode, blocked profile). */
export function readStored(key: StorageKey): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

/** Best effort: the value still applies for this session if it cannot persist. */
export function writeStored(key: StorageKey, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Storage unavailable; nothing to do.
  }
}

/** Best effort, like `writeStored`. */
export function removeStored(key: StorageKey): void {
  try {
    localStorage.removeItem(key)
  } catch {
    // Storage unavailable; nothing to do.
  }
}

/** What lives in `sessionStorage`: this tab only, gone when it closes. */
export const SESSION_KEYS = {
  /** The top-up in progress: its Idempotency-Key and what it created, never a card or a token (LLD §5.5). */
  topupAttempt: "devsms.topup.attempt",
} as const

export type SessionKey = (typeof SESSION_KEYS)[keyof typeof SESSION_KEYS]

/** `null` when absent or unavailable. */
export function readSession(key: SessionKey): string | null {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

/** Best effort: `false` when storage refused it (the caller may keep it in memory). */
export function writeSession(key: SessionKey, value: string): boolean {
  try {
    sessionStorage.setItem(key, value)
    return true
  } catch {
    return false
  }
}

/** Best effort. */
export function removeSession(key: SessionKey): void {
  try {
    sessionStorage.removeItem(key)
  } catch {
    // Storage unavailable; nothing to do.
  }
}

